import { DeviceType } from '@frodo/models/types';
import {
  CONSENT_MANAGER_UPDATE_EVENT,
  ConsentState,
} from '@frodo/lib/utils/consent';

export const CONSENT_LOG_EVENT = {
  Shown: 'ConsentBannerShown',
  AlreadySeen: 'ConsentBannerAlreadySeen',
  Accepted: 'ConsentBannerAccepted',
  Rejected: 'ConsentBannerRejected',
} as const;

export type ConsentLogEvent =
  (typeof CONSENT_LOG_EVENT)[keyof typeof CONSENT_LOG_EVENT];

export const NON_ESSENTIAL_CATEGORY_IDS = ['15', '16', '17'] as const;

export type ConsentLogContext = {
  store: string;
  device: DeviceType;
  isBot: boolean;
};

let baseContext: ConsentLogContext | null = null;
let v2SubscriptionsRegistered = false;

export const setConsentLogContext = (context: ConsentLogContext) => {
  baseContext = context;
};

type DdLogger = {
  info: (message: string, context?: Record<string, unknown>) => void;
};

type DdLogs = {
  logger?: DdLogger;
};

const whenDdLoggerReady = (
  callback: (logger: DdLogger) => void,
  attemptsLeft = 50,
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const logger = (window.DD_LOGS as DdLogs | undefined)?.logger;

  if (logger) {
    callback(logger);

    return;
  }

  if (attemptsLeft <= 0) {
    return;
  }

  setTimeout(() => whenDdLoggerReady(callback, attemptsLeft - 1), 100);
};

export const logConsentEvent = (
  event: ConsentLogEvent,
  extra?: Record<string, unknown>,
) => {
  whenDdLoggerReady(logger => {
    if (!baseContext) {
      return;
    }

    logger.info(event, { ...baseContext, ...extra });
  });
};

const reportConsentBannerAlreadySeen = (state: ConsentState) => {
  window.consentBannerAlreadySeenReported ??= false;

  if (window.consentBannerAlreadySeenReported) {
    return;
  }

  window.consentBannerAlreadySeenReported = true;
  logConsentEvent(CONSENT_LOG_EVENT.AlreadySeen, {
    consentStatus: state.status,
    categories: state.categories.filter(category =>
      NON_ESSENTIAL_CATEGORY_IDS.includes(
        category as (typeof NON_ESSENTIAL_CATEGORY_IDS)[number],
      ),
    ),
  });
};

export const reportConsentBannerShown = () => {
  window.consentBannerShownReported ??= false;

  if (window.consentBannerShownReported) {
    return;
  }

  window.consentBannerShownReported = true;
  logConsentEvent(CONSENT_LOG_EVENT.Shown);
};

/** Returns an unsubscribe callback when the telemetry got registered. */
export const registerConsentTelemetry = (): (() => void) | undefined => {
  if (typeof window === 'undefined' || v2SubscriptionsRegistered) {
    return undefined;
  }

  v2SubscriptionsRegistered = true;
  window.consentBannerShownReported ??= false;
  window.consentBannerAlreadySeenReported ??= false;

  const onConsentUpdate = (event: Event) => {
    const state = (event as CustomEvent<ConsentState>).detail;
    const accepted = state.categories.filter(category =>
      NON_ESSENTIAL_CATEGORY_IDS.includes(
        category as (typeof NON_ESSENTIAL_CATEGORY_IDS)[number],
      ),
    );

    logConsentEvent(
      accepted.length > 0
        ? CONSENT_LOG_EVENT.Accepted
        : CONSENT_LOG_EVENT.Rejected,
      accepted.length > 0 ? { categories: accepted } : undefined,
    );
  };

  window.addEventListener(CONSENT_MANAGER_UPDATE_EVENT, onConsentUpdate);

  if (window.consentManager?.ready) {
    const state = window.consentManager.get();
    if (state.status === 'unset') {
      reportConsentBannerShown();
    } else {
      reportConsentBannerAlreadySeen(state);
    }
  }

  return () => {
    window.removeEventListener(CONSENT_MANAGER_UPDATE_EVENT, onConsentUpdate);
    v2SubscriptionsRegistered = false;
  };
};
